/**
 * Sincronización multi-tab de la sesión.
 *
 * Si en Tab A el usuario hace logout (manual o por timeout), Tab B debe
 * detectarlo y redirigir a /login automáticamente. La forma estándar en
 * SPAs es escuchar el evento `storage` del navegador — se dispara en TODAS
 * las pestañas EXCEPTO la que originó el cambio.
 *
 * El store `useSessionStore` persiste en localStorage bajo
 * `riga-traza-session-v1`. Cuando una tab limpia esa key (logout), las
 * otras tabs reciben el evento `storage` con `newValue === null`.
 *
 * GAMP 5: garantiza que el cierre de sesión sea consistente en todas las
 * pestañas — si el usuario hace logout en una, no queda otra abierta con
 * sesión "viva" en memoria.
 */

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useSessionStore } from '@/stores/session';

const SESSION_KEY = 'riga-traza-session-v1';

export function useMultiTabSessionSync(): void {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const usuario = useSessionStore((s) => s.usuario);

  useEffect(() => {
    // Solo nos interesa el sync cuando hay sesión activa
    if (!usuario) return;

    const handler = (e: StorageEvent) => {
      if (e.key !== SESSION_KEY) return;
      // newValue null = removeItem (logout completo)
      if (e.newValue === null) {
        queryClient.clear();
        void navigate('/login', { replace: true });
        return;
      }
      // El store tiene un campo `usuario` adentro. Si ahora es null en otra
      // pestaña (sin remove key), también es logout — chequeamos parseando.
      try {
        const parsed = JSON.parse(e.newValue) as {
          state?: { usuario?: unknown };
        };
        if (parsed.state?.usuario === null || parsed.state?.usuario === undefined) {
          queryClient.clear();
          void navigate('/login', { replace: true });
        }
      } catch {
        // JSON inválido en localStorage — ignorar silenciosamente
      }
    };

    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, [usuario, navigate, queryClient]);
}
